import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Eye, EyeOff } from 'lucide-react';

const Input = ({
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  label,
  error,
  helperText,
  icon: Icon,
  required = false,
  disabled = false,
  rows,
  ...rest
}) => {
  const { theme } = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const isTextarea = type === 'textarea';
  const isPassword = type === 'password';

  const styles = {
    container: {
      marginBottom: '20px',
      width: '100%',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: Icon ? '11px 14px 11px 42px' : '11px 14px',
      fontSize: '14px',
      borderRadius: '8px',
      border: `2px solid ${error ? theme.colors.error : isFocused ? theme.colors.primary : theme.colors.border}`,
      backgroundColor: disabled ? theme.colors.backgroundSecondary : theme.colors.inputBg,
      color: theme.colors.text,
      transition: 'all 0.2s',
      outline: 'none',
      fontFamily: 'inherit',
      resize: isTextarea ? 'vertical' : 'none',
      colorScheme: theme.name === 'dark' ? 'dark' : 'light',
    },
    iconWrapper: {
      position: 'absolute',
      left: '12px',
      color: theme.colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
    },
    togglePassword: {
      position: 'absolute',
      right: '12px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      padding: '4px',
    },
    error: {
      marginTop: '6px',
      fontSize: '13px',
      color: theme.colors.error,
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    helperText: {
      marginTop: '6px',
      fontSize: '13px',
      color: theme.colors.textSecondary,
    },
  };

  const InputElement = isTextarea ? 'textarea' : 'input';

  return (
    <div style={styles.container}>
      {label && (
        <label style={styles.label}>
          {label}
          {required && <span style={{ color: theme.colors.error }}> *</span>}
        </label>
      )}
      <div style={styles.inputWrapper}>
        {Icon && (
          <div style={styles.iconWrapper}>
            <Icon size={18} />
          </div>
        )}
        <InputElement
          type={isPassword ? (showPassword ? 'text' : 'password') : (isTextarea ? undefined : type)}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={rows || 4}
          style={styles.input}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            style={styles.togglePassword}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && <div style={styles.error}>{error}</div>}
      {helperText && !error && <div style={styles.helperText}>{helperText}</div>}
    </div>
  );
};

export default Input;
